// code-guardian-ignore-duplicates: shared structural skeleton / cross-app type mirror — intentional pattern duplication (scheduler lifecycle, CRS-gateway wrappers, web/api type mirrors with no shared package), not copy-pasted logic; consolidating (base class / shared types pkg) is a separate refactor
/**
 * 10-tap-on-logo cache flush gesture (web).
 *
 * Hidden UX: any user can tap the ChannelPlex logo 10 times within a 10-second
 * window on the login screen to flush the server-side caches. Use when admins
 * have updated holidays / leave types / project master / reporting hierarchy
 * and end users need to see the change before the next TTL refresh.
 *
 * Wire up: `<Logo onClick={onTap} />` and render `notice` near the logo.
 *
 * Backend rate limits (apps/api/src/middleware/rate-limit-public-flush.middleware.ts):
 *   - 1 request per 5 min per IP
 *   - 10 requests per 30 min globally
 * If rate-limited, the user sees a friendly "try again later" message.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const TAP_THRESHOLD = 10;
const TAP_WINDOW_MS = 10_000;
const NOTICE_DISPLAY_MS = 3_000;

interface UseLogoTapFlushResult {
  onTap: () => void;
  notice: string | null;
}

export function useLogoTapFlush(): UseLogoTapFlushResult {
  const tapCountRef = useRef(0);
  const windowStartRef = useRef(0);
  const [notice, setNotice] = useState<string | null>(null);
  const noticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear the notice timer on unmount so a stale timer can't update state on a
  // dead component (React StrictMode mounts twice in dev — without cleanup the
  // notice would flash twice during local dev).
  useEffect(() => {
    return () => {
      if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    };
  }, []);

  const showNotice = useCallback((msg: string) => {
    setNotice(msg);
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    noticeTimerRef.current = setTimeout(() => {
      setNotice(null);
      noticeTimerRef.current = null;
    }, NOTICE_DISPLAY_MS);
  }, []);

  const callFlush = useCallback(async () => {
    try {
      // Use VITE_API_URL if available (matches services/api.ts convention).
      // Falls back to relative '/api' which works in production where the API
      // is served from the same host.
      const rawApiUrl = (import.meta as any).env?.VITE_API_URL as string | undefined;
      const base = rawApiUrl ? rawApiUrl.replace(/\/+$/, '') : '/api';
      const url = `${base}/cache/refresh-public`;
      const response = await fetch(url, { method: 'POST' });
      if (response.status === 429) {
        showNotice('Cache refresh is rate-limited — try again later.');
        return;
      }
      if (!response.ok) {
        showNotice('Cache refresh failed.');
        return;
      }
      showNotice('Cache refreshed.');
    } catch (err) {
      console.error('[useLogoTapFlush] flush failed:', err);
      showNotice('Cache refresh failed (network).');
    }
  }, [showNotice]);

  const onTap = useCallback(() => {
    const now = Date.now();
    // Reset window if too much time has passed since the first tap.
    if (now - windowStartRef.current > TAP_WINDOW_MS) {
      tapCountRef.current = 1;
      windowStartRef.current = now;
      return;
    }
    tapCountRef.current += 1;
    if (tapCountRef.current >= TAP_THRESHOLD) {
      tapCountRef.current = 0;
      windowStartRef.current = 0;
      void callFlush();
    }
  }, [callFlush]);

  return { onTap, notice };
}
